import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useI18n } from "../I18nContext";

function takeOAuthFlag() {
  if (typeof window === "undefined") return { googleOk: false, googleErr: false, microsoftOk: false, microsoftErr: false };
  const url = new URL(window.location.href);
  const googleOk = url.searchParams.get("google_connected");
  const googleErr = url.searchParams.get("google_error");
  const microsoftOk = url.searchParams.get("microsoft_connected");
  const microsoftErr = url.searchParams.get("microsoft_error");
  if (googleOk || googleErr || microsoftOk || microsoftErr) {
    url.searchParams.delete("google_connected");
    url.searchParams.delete("google_error");
    url.searchParams.delete("microsoft_connected");
    url.searchParams.delete("microsoft_error");
    window.history.replaceState(null, "", url.toString());
  }
  return { googleOk: !!googleOk, googleErr: !!googleErr, microsoftOk: !!microsoftOk, microsoftErr: !!microsoftErr };
}

export default function GoogleConnectBlock() {
  const { t } = useI18n();
  const [google, setGoogle] = useState(null);
  const [microsoft, setMicrosoft] = useState(null);
  const [flag] = useState(takeOAuthFlag);
  const [err, setErr] = useState(
    flag?.googleErr ? t("google.errorGeneric") : flag?.microsoftErr ? t("microsoft.errorGeneric") : "",
  );
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.get("/oauth/google/status")
      .then((r) => setGoogle(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    api.get("/oauth/microsoft/status")
      .then((r) => setMicrosoft(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [reloadKey]);

  const connect = (path) => {
    setErr("");
    api.get(path)
      .then((r) => { if (r.data.url) window.location.href = r.data.url; })
      .catch((e) => {
        if (e.response?.status === 503 && path.includes("microsoft")) {
          setErr(t("microsoft.notConfigured"));
          return;
        }
        if (e.response?.status === 503 && path.includes("google")) {
          setErr(t("google.notConfigured"));
          return;
        }
        setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      });
  };

  const disconnect = (path, confirmKey) => {
    if (!window.confirm(t(confirmKey))) return;
    api.delete(path)
      .then(() => setReloadKey((k) => k + 1))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };

  return (
    <div className="tb-block" style={{ marginBottom: "1.5rem" }} data-testid="google-connect-block">
      <h3 style={{ marginTop: 0, marginBottom: 16, fontFamily: "var(--font-display)" }}>{t("google.title")}</h3>
      <p className="tb-muted" style={{ marginTop: 0 }}>{t("google.hint")}</p>
      {err && <div className="tb-form-error" style={{ marginBottom: 10 }} data-testid="calendar-connect-error">{err}</div>}
      {(google === null || microsoft === null) && <div className="tb-loading">{t("common.loading")}</div>}
      {google && microsoft && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
          {google.connected ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <span className="tb-badge good" data-testid="google-connected-badge">✓ {t("google.connected")}</span>
                <div className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 6 }}>{google.email}</div>
                {google.has_calendar_scope === false && (
                  <div className="tb-form-error" style={{ marginTop: 8, fontSize: "0.85rem" }} data-testid="google-missing-scope">
                    ⚠ {t("google.missingCalendarScope")}
                  </div>
                )}
              </div>
              <button type="button" className="tb-ghost-btn" onClick={() => disconnect("/oauth/google", "google.confirmDisconnect")} data-testid="google-disconnect">{t("google.disconnect")}</button>
            </div>
          ) : (
            <button type="button" className="tb-primary-btn" onClick={() => connect("/oauth/google/start")} data-testid="google-connect" style={{ color: "#0a0d12", width: "fit-content" }}>
              {t("google.connectCta")}
            </button>
          )}
          {microsoft.connected ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <span className="tb-badge good" data-testid="microsoft-connected-badge">✓ {t("microsoft.connected")}</span>
                <div className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 6 }}>{microsoft.email}</div>
                {microsoft.has_calendar_scope === false && (
                  <div className="tb-form-error" style={{ marginTop: 8, fontSize: "0.85rem" }} data-testid="microsoft-missing-scope">
                    ⚠ {t("microsoft.missingCalendarScope")}
                  </div>
                )}
              </div>
              <button type="button" className="tb-ghost-btn" onClick={() => disconnect("/oauth/microsoft", "microsoft.confirmDisconnect")} data-testid="microsoft-disconnect">{t("microsoft.disconnect")}</button>
            </div>
          ) : (
            <button type="button" className="tb-primary-btn" onClick={() => connect("/oauth/microsoft/start")} data-testid="microsoft-connect" style={{ color: "#0a0d12", width: "fit-content" }}>
              {t("microsoft.connectCta")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
